import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/datetime';
import { useClaimJob } from '@/hooks/useReviewQueue';
import type { ReviewQueueItem as ReviewQueueItemType } from '@/hooks/useReviewQueue';
import { agingMs, formatAging, agingColor } from './reviewQueueAging';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ReviewQueueItemProps {
  item: ReviewQueueItemType;
  canClaim: boolean;
  currentUserId: string | undefined;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Single queue item row.
 * - Clicking anywhere on the row navigates to /jobs/:id
 * - "Start Review" claims the job then navigates (claim + navigate in one action)
 * - "Continue Review" navigates directly (already claimed by this user)
 * - Aging indicator turns red when > 48h (overdue threshold from FR-086)
 */
export function ReviewQueueItem({ item, canClaim, currentUserId }: ReviewQueueItemProps) {
  const navigate = useNavigate();
  const { mutate: claimJob, isPending } = useClaimJob();
  const ms = agingMs(item.completed_at, item.created_at);
  const isMine = item.reviewed_by === currentUserId;
  const isOverdue = ms > 48 * 3_600_000;

  function handleClaim(e: React.MouseEvent) {
    e.stopPropagation();
    claimJob(item.id, {
      onSuccess: () => { navigate(`/jobs/${item.id}`); },
      onError: () => toast.error('Failed to claim contract. Please try again.'),
    });
  }

  function handleContinue(e: React.MouseEvent) {
    e.stopPropagation();
    navigate(`/jobs/${item.id}`);
  }

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => { navigate(`/jobs/${item.id}`); }}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/jobs/${item.id}`); }}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 cursor-pointer hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors sm:flex-row sm:items-center sm:gap-4"
    >
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium text-sm" title={item.file_name}>{item.file_name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          <span className="font-mono">{fmtDateTime(item.created_at)}</span>
          {' · '}
          {item.submitted_by_name ?? 'Unknown'} · {item.blocking_count} blocking, {item.alert_count} alerts
        </p>
      </div>

      <div className={cn('flex items-center gap-1 text-xs font-medium', agingColor(ms))}>
        {isOverdue && (
          <span aria-label="Overdue — waiting more than 48 hours" title="Overdue">!</span>
        )}
        Waiting {formatAging(ms)}
      </div>

      <div className="text-xs text-muted-foreground">
        {item.reviewed_by_name
          ? isMine
            ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">Assigned to you</span>
            : <span>{item.reviewed_by_name}</span>
          : <span className="italic">Unassigned</span>}
      </div>

      {canClaim && (
        isMine ? (
          <button
            type="button"
            onClick={handleContinue}
            aria-label={`Continue review for ${item.file_name}`}
            className="shrink-0 rounded px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Continue Review
          </button>
        ) : (
          <button
            type="button"
            onClick={handleClaim}
            disabled={isPending}
            aria-label={`Claim and start review for ${item.file_name}`}
            className="shrink-0 rounded px-3 py-1 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isPending ? 'Claiming…' : 'Start Review'}
          </button>
        )
      )}
    </div>
  );
}
